import { supabase } from './supabase'

// ── Types ──────────────────────────────────────────────────────────────────

export type NotificationType = 'payment' | 'reminder' | 'trial' | 'summary' | 'system'

export interface AppNotification {
    id: string
    seller_id: string
    type: NotificationType
    title: string
    message: string
    read: boolean
    link: string | null
    created_at: string
}

// ── Queries ────────────────────────────────────────────────────────────────

/** Fetch all notifications for the current seller, newest first */
export async function fetchNotifications(): Promise<{ data: AppNotification[]; error: string | null }> {
    const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)

    if (error) return { data: [], error: error.message }
    return { data: (data ?? []) as AppNotification[], error: null }
}

/** Count unread notifications */
export async function fetchUnreadCount(): Promise<number> {
    const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('read', false)

    return count ?? 0
}

/** Mark a single notification as read */
export async function markNotificationRead(id: string): Promise<void> {
    await supabase.from('notifications').update({ read: true }).eq('id', id)
}

/** Mark all notifications as read for current seller */
export async function markAllNotificationsRead(): Promise<{ error: string | null }> {
    const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('read', false)

    return { error: error?.message ?? null }
}

/** Insert a welcome notification for a new seller (call after sign-up profile insert) */
export async function insertWelcomeNotification(sellerId: string): Promise<void> {
    await supabase.from('notifications').insert({
        seller_id: sellerId,
        type: 'system',
        title: 'Welcome to MoniFlow! 🎉',
        message: 'Your account is ready. Start by creating your first order.',
        link: '/seller/orders/new',
    })
}

/** Insert a notification after an order action (create / update / delete) */
export async function insertOrderNotification(
    sellerId: string,
    action: 'created' | 'updated' | 'deleted',
    customerName: string,
    orderId?: string,
): Promise<void> {
    const titles = {
        created: `New order for ${customerName}`,
        updated: `Order updated for ${customerName}`,
        deleted: `Order removed for ${customerName}`,
    }
    const messages = {
        created: `A new order was successfully recorded for ${customerName}.`,
        updated: `Order details have been updated for ${customerName}.`,
        deleted: `The order for ${customerName} has been deleted.`,
    }
    await supabase.from('notifications').insert({
        seller_id: sellerId,
        type: 'payment',
        title: titles[action],
        message: messages[action],
        link: orderId ? `/seller/orders/${orderId}` : '/seller/orders',
    })
}
